import { User, UserStatus } from '@repo-level/core-front';

import { CmsStardustRush } from 'api/cms/stardustRush';
import {
  COMMON_VIP_GROUPS,
  groupMember,
  someGroupMember,
  USER_GROUP,
  vipGroups,
} from './user';

import pagePanelDefault from 'assets/images/vip/vipLogos/vip-logo-pvip.webp';
import sidebarPanelDefault from 'assets/images/vip/vipLogos/sidebar/aside-vip-logo-default.webp';
import menuPanelDefault from 'assets/images/vip/vipLogos/menu/menu-vip-logo-default.webp';
import pagePanelGold from 'assets/images/vip/vipLogos/vip-logo-gold-current.webp';
import sidebarPanelGold from 'assets/images/vip/vipLogos/sidebar/aside-vip-logo-gold.webp';
import menuPanelGold from 'assets/images/vip/vipLogos/menu/menu-vip-logo-gold.webp';
import pagePanelPlatinum from 'assets/images/vip/vipLogos/vip-logo-platinum-current.webp';
import sidebarPanelPlatinum from 'assets/images/vip/vipLogos/sidebar/aside-vip-logo-platinum.webp';
import menuPanelPlatinum from 'assets/images/vip/vipLogos/menu/menu-vip-logo-platinum.webp';
import pagePanelDiamond from 'assets/images/vip/vipLogos/vip-logo-diamond-current.webp';
import sidebarPanelDiamond from 'assets/images/vip/vipLogos/sidebar/aside-vip-logo-diamond.webp';
import menuPanelDiamond from 'assets/images/vip/vipLogos/menu/menu-vip-logo-diamond.webp';

const SDR_GROUP_KEY = 'SDR';
const COMPOINTS_KOEF = 100;

const getUserVipGroup = (statuses: UserStatus[]) =>
  statuses.find(({ id }) => vipGroups.includes(id as USER_GROUP))?.id;

export interface StardustRushStaticData {
  pagePanelImg: string;
  sidebarPanelImg: string;
  menuPanelImg: string;
  vipTypeName: string;
  prizeText: string;
}

export const stardustRushStaticDataMap: Record<
  string,
  StardustRushStaticData
> = {
  [USER_GROUP.PVIP]: {
    pagePanelImg: pagePanelDefault,
    sidebarPanelImg: sidebarPanelDefault,
    menuPanelImg: menuPanelDefault,
    vipTypeName: '',
    prizeText: '',
  },
  [USER_GROUP.GOLD]: {
    pagePanelImg: pagePanelGold,
    sidebarPanelImg: sidebarPanelGold,
    menuPanelImg: menuPanelGold,
    vipTypeName: 'Gold',
    prizeText: 'vipNamePrize.gold',
  },
  [USER_GROUP.PLATINUM]: {
    pagePanelImg: pagePanelPlatinum,
    sidebarPanelImg: sidebarPanelPlatinum,
    menuPanelImg: menuPanelPlatinum,
    vipTypeName: 'Platinum',
    prizeText: 'vipNamePrize.platinum',
  },
  [USER_GROUP.DIAMOND]: {
    pagePanelImg: pagePanelDiamond,
    sidebarPanelImg: sidebarPanelDiamond,
    menuPanelImg: menuPanelDiamond,
    vipTypeName: 'Diamond',
    prizeText: 'vipNamePrize.diamond',
  },
};

export const getStardustRushStaticData = (statuses: UserStatus[]) => {
  const vipGroup = getUserVipGroup(statuses);

  if (vipGroup) return stardustRushStaticDataMap[vipGroup];

  return stardustRushStaticDataMap[USER_GROUP.PVIP];
};

export const getUserCompointsBySDRGroup = (statuses: UserStatus[]) => {
  const userSDRGroup = [...statuses]
    .reverse()
    .find(({ id }) => id.includes(SDR_GROUP_KEY));

  if (!userSDRGroup) return 0;

  const compointsNumberPart = Number(userSDRGroup.id.split('_').slice(-1)[0]);

  if (isNaN(compointsNumberPart)) return 0;

  return compointsNumberPart * COMPOINTS_KOEF;
};

export const getUserTarget = (
  statuses: UserStatus[],
  targets: Pick<CmsStardustRush, 'vip1Goal' | 'vip2Goal' | 'vip3Goal'>
) => {
  const vipGroup = getUserVipGroup(statuses);

  if (!vipGroup) return;

  const targetByGroupMap: Record<string, number> = {
    [USER_GROUP.GOLD]: targets.vip1Goal,
    [USER_GROUP.PLATINUM]: targets.vip2Goal,
    [USER_GROUP.DIAMOND]: targets.vip3Goal,
  };

  return targetByGroupMap[vipGroup];
};

export const calculateUserProgress = (compoints: number, target: number) =>
  Math.floor((compoints / target) * 100);

export enum StardustRushStatus {
  PVIP = 'pVIP',
  ACTIVE = 'ACTIVE',
  COMPLETE = 'COMPLETE',
}

export const getStardustRushStatus = (user: User) => {
  if (groupMember(USER_GROUP.SDR_COMPLETE)(user))
    return StardustRushStatus.COMPLETE;
  if (someGroupMember(COMMON_VIP_GROUPS)(user))
    return StardustRushStatus.ACTIVE;
  return StardustRushStatus.PVIP;
};
